"use client";

import { useTheme } from "next-themes";
import { useEffect, useState, useCallback } from "react";
import { Sun, Moon } from "lucide-react";
import { Skeleton } from "@shared/src/components/ui/skeleton";
import { cn } from "@shared/src/utils/cn";
export function ThemeSwitcher({ className }: { className?: string }) {
  const [mounted, setMounted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { setTheme, theme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleThemeChange = useCallback(
    (newTheme: "light" | "dark") => {
      if (isTransitioning) return;

      setIsTransitioning(true);
      setTheme(newTheme);

      // Reset transition state after animation completes
      setTimeout(() => {
        setIsTransitioning(false);
      }, 200);
    },
    [isTransitioning, setTheme],
  );

  if (!mounted) {
    return <Skeleton className="h-[36px] w-[62px] rounded-full bg-surfaces-bg-secondary" />;
  }

  return (
    <div
      className={cn(
        "relative flex w-[62px] h-[36px] justify-between items-center rounded-full p-[10px] gap-[10px] bg-surfaces-bg-secondary",
        className,
      )}
      style={{
        transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <button
        className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full text-text-primary hover:text-text-brand"
        style={{
          transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onClick={() => handleThemeChange("light")}
        disabled={isTransitioning}
        aria-label="Light Mode"
      >
        <Sun
          className={`h-4 w-4 ${theme === "light" ? "text-text-brand scale-110" : "scale-100 opacity-60"}`}
          style={{
            transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </button>
      <button
        className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full text-text-primary hover:text-text-brand"
        style={{
          transition: "color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onClick={() => handleThemeChange("dark")}
        disabled={isTransitioning}
        aria-label="Dark Mode"
      >
        <Moon
          className={`h-4 w-4 ${theme === "dark" ? "text-text-brand scale-110" : "scale-100 opacity-60"}`}
          style={{
            transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </button>
      {/* Moving indicator */}
      <div
        className={`absolute left-[6px] h-6 w-6 ${theme === "dark" ? "translate-x-[26px]" : ""}`}
        style={{
          transition: "transform 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Solid ball */}
        <div
          className="absolute inset-0 border rounded-full border-border-focus bg-surfaces-bg-primary"
          style={{
            transition:
              "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
        {/* Perfect fitting outline */}
        <div
          className="absolute inset-0 rounded-full border-[1.5px] border-dashed border-primary-500 animate-[spin_10s_linear_infinite]"
          style={{ padding: "0.5px" }}
        />
      </div>
    </div>
  );
}
